"use client";

import * as React from "react";
import {
  BookCheck,
  X,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronsRight,
  Bookmark,
} from "lucide-react";
import { cn } from "@/lib/utils";
import ResizeHandle from "./ResizeHandle";

const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

/**
 * 풀이 모드 우측 chrome 공유 컴포넌트 (JCG-2065 / JCG-2105).
 *
 * ArticleShell 의 풀이 모드 확장 블록 + 숨기기 레일 블록을 verbatim 으로 추출한 것.
 * ArticleShell 과 ForumProblemShell 이 동일한 풀이 chrome 을 공유해 외관·동작이 100% 일치하도록 한다.
 * className·구조·주석은 ArticleShell 원본 그대로 — restyle 금지(공유 컴포넌트라 parity 자동 보장).
 *
 * ProblemAnswer 가 풀이 본문/오답노트 편집기를 solutionSlot/errorNoteSlot 에 createPortal 로 그린다.
 * "숨기기" 시 확장 블록은 display:none(`hidden`)으로 보존(언마운트 아님 → errorNoteSlot ref 유지).
 */
interface SolutionAsideProps {
  activeSolutionId: string | null;
  /** 비저장 관리자 미리보기에서는 오답노트 조작을 표시하지 않는다. */
  showErrorNote?: boolean;
  problemLabel?: string;
  solutionCollapsed: boolean;
  onHide: () => void;
  onExpand: () => void;
  onExit: () => void;
  wrongOpen: boolean;
  setWrongOpen: (open: boolean) => void;
  splitRatio: number;
  solutionSplitRef: React.RefObject<HTMLDivElement | null>;
  onResizeSolutionSplit: (clientY: number) => void;
  setSolutionSlot: (el: HTMLElement | null) => void;
  setErrorNoteSlot: (el: HTMLElement | null) => void;
}

export default function SolutionAside({
  activeSolutionId,
  showErrorNote = true,
  problemLabel,
  solutionCollapsed,
  onHide,
  onExpand,
  onExit,
  wrongOpen,
  setWrongOpen,
  splitRatio,
  solutionSplitRef,
  onResizeSolutionSplit,
  setSolutionSlot,
  setErrorNoteSlot,
}: SolutionAsideProps) {
  const solutionScrollContainerRef = React.useRef<HTMLDivElement | null>(null);
  const attachedSolutionSlotRef = React.useRef<HTMLElement | null>(null);
  const attachedErrorNoteSlotRef = React.useRef<HTMLElement | null>(null);
  const attachSolutionSlot = React.useCallback(
    (element: HTMLElement | null) => {
      if (!element || attachedSolutionSlotRef.current === element) return;
      attachedSolutionSlotRef.current = element;
      setSolutionSlot(element);
    },
    [setSolutionSlot],
  );
  const attachErrorNoteSlot = React.useCallback(
    (element: HTMLElement | null) => {
      if (!element || attachedErrorNoteSlotRef.current === element) return;
      attachedErrorNoteSlotRef.current = element;
      setErrorNoteSlot(element);
    },
    [setErrorNoteSlot],
  );

  // 패널은 mount 유지되므로, 다른 문제 풀이로 교체될 때 이전 scrollTop 을 직접 버린다.
  useIsomorphicLayoutEffect(() => {
    if (!activeSolutionId) return;
    const container = solutionScrollContainerRef.current;
    if (!container) return;
    container.scrollTop = 0;
    container.scrollLeft = 0;
  }, [activeSolutionId]);

  return (
    <>
      {/* 풀이 모드 chrome — 셸이 헤더·50/50 분할·드래그 핸들을 소유하고,
          ProblemAnswer 가 풀이 본문/오답노트 편집기를 solutionSlot/errorNoteSlot 에 portal.
          "숨기기" 시 display:none 으로 보존(슬롯 ref 유지 → 오답노트 편집기 상태 보존) + 레일 노출. */}
      <div className={cn("flex min-h-0 flex-1 flex-col", solutionCollapsed && "hidden")}>
        {/* 헤더 */}
        <div className="flex shrink-0 items-center justify-between px-4 pb-2.5 pt-3.5">
          <div className="flex min-w-0 items-center gap-2">
            <BookCheck className="h-[18px] w-[18px] shrink-0 text-amber-500" />
            <span className="shrink-0 text-[15px] font-extrabold text-foreground">풀이</span>
            <span title={problemLabel} translate={problemLabel ? "no" : undefined} aria-live={problemLabel ? "polite" : undefined} className="truncate rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700 dark:bg-amber-950/50 dark:text-amber-300">
              {problemLabel ?? "방금 푼 문제"}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {/* 숨기기 — 풀이 모드 유지한 채 레일로 최소화 */}
            <button
              type="button"
              onClick={onHide}
              title="풀이 숨기기"
              className="inline-flex h-8 items-center gap-1 rounded-lg border border-border bg-card px-2 text-[12px] font-bold text-muted-foreground transition-colors hover:text-foreground"
            >
              <ChevronsRight className="h-[15px] w-[15px]" />
              숨기기
            </button>
            {/* 풀이 닫기 — 학습 도구 모드로 완전 복귀 */}
            <button
              type="button"
              onClick={onExit}
              title="풀이 닫기"
              className="inline-flex h-8 items-center gap-1 rounded-lg border border-border bg-card px-2 text-[12px] font-bold text-muted-foreground transition-colors hover:text-foreground"
            >
              <X className="h-[15px] w-[15px]" />
              닫기
            </button>
          </div>
        </div>

        {!showErrorNote || !wrongOpen ? (
          // 오답노트 닫힘 — 풀이 본문은 위에서 독립 스크롤, "오답노트 열기"는 하단 고정(경계선 구분).
          <div className="flex min-h-0 flex-1 flex-col">
            <div
              ref={solutionScrollContainerRef}
              className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3.5 pb-3"
            >
              <div ref={attachSolutionSlot} className="min-h-0" />
            </div>
            {showErrorNote && (
              <div className="flex shrink-0 justify-center border-t border-border px-3.5 pb-3.5 pt-3">
                <button
                  type="button"
                  onClick={() => setWrongOpen(true)}
                  className="flex items-center gap-2 rounded-xl border border-border bg-card px-5 py-2.5 transition-colors hover:border-amber-300 dark:hover:border-amber-700"
                >
                  <Bookmark className="h-4 w-4 shrink-0 text-amber-500" />
                  <span className="text-[13.5px] font-extrabold text-foreground">오답노트 열기</span>
                  <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              </div>
            )}
          </div>
        ) : (
          // 오답노트 열림 — 세로 50/50 분할(드래그로 비율 조절). 위 풀이 / 아래 오답노트.
          <div ref={solutionSplitRef} className="flex min-h-0 flex-1 flex-col px-3.5 pb-3.5">
            <div
              ref={solutionScrollContainerRef}
              className="min-h-0 overflow-y-auto overscroll-contain pr-1"
              style={{ flex: `0 0 ${splitRatio * 100}%` }}
            >
              <div ref={attachSolutionSlot} className="min-h-0" />
            </div>
            <ResizeHandle orientation="h" onResize={onResizeSolutionSplit} />
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="flex shrink-0 items-center justify-between px-0.5 pb-2">
                <div className="flex items-center gap-1.5">
                  <Bookmark className="h-[15px] w-[15px] fill-amber-500 text-amber-500" />
                  <span className="text-[13.5px] font-extrabold text-foreground">오답노트</span>
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700 dark:bg-amber-950/50 dark:text-amber-300">
                    이 문제
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setWrongOpen(false)}
                  title="오답노트 접기"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground transition-colors hover:text-foreground"
                >
                  <ChevronDown className="h-[15px] w-[15px]" />
                </button>
              </div>
              <div
                ref={attachErrorNoteSlot}
                className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
              />
            </div>
          </div>
        )}
      </div>

      {/* 풀이 레일(숨기기 상태) — 클릭 시 다시 펼침, 아래 X 로 완전 닫기 */}
      {solutionCollapsed && (
        <div className="flex h-full flex-col items-center gap-2 py-3">
          <button
            type="button"
            onClick={onExpand}
            title="풀이 펼치기"
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card text-amber-500 transition-colors hover:border-amber-300 dark:hover:border-amber-700"
          >
            <ChevronLeft className="h-[18px] w-[18px]" />
          </button>
          {/* 아이콘만(클릭해도 펼침) */}
          <button
            type="button"
            onClick={onExpand}
            title="풀이 펼치기"
            className="flex flex-1 flex-col items-center justify-center rounded-lg px-1 transition-colors hover:bg-amber-50 dark:hover:bg-amber-950/30"
          >
            <BookCheck className="h-[18px] w-[18px] shrink-0 text-amber-500" />
          </button>
          {/* 풀이 닫기 — 완전 종료 */}
          <button
            type="button"
            onClick={onExit}
            title="풀이 닫기"
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </>
  );
}
